import {
    questionCustomisationFilterPrefix,
    QuestionCustomisationFilterType,
} from '../../../api/models/survey';
import {
    AttendeeSurvey,
    isWaiverQuestion,
    SurveyQuestion,
} from '../../../models/survey';
import { isCannedQuestion, isRequiredQuestion } from '../../../utils/surveys';
import {
    CANNED_QUESTION_COMPANY,
    CANNED_QUESTION_WORK_ADDRESS,
} from '../constants';

export const cleanUpDuplicateTaxesQuestions = (
    questionsList: Array<Pick<SurveyQuestion, 'name'>>,
) => {
    const duplicateQuestionList: Array<string> = [
        CANNED_QUESTION_WORK_ADDRESS,
        CANNED_QUESTION_COMPANY,
    ];

    return questionsList.filter(
        (element) => !duplicateQuestionList.includes(element.name),
    );
};

export const canUseAndroidMessage = () => window.android !== undefined;

export const getOptionalQuestions = <Question extends Array<SurveyQuestion>>(
    questions: Question,
) => questions.filter((question) => !isRequiredQuestion(question, questions));

export const getOptionalAndCannedQuestions = (
    questions: Array<SurveyQuestion>,
) =>
    questions.filter(
        (question) =>
            !isRequiredQuestion(question, questions) ||
            isCannedQuestion(question),
    );

export const getAllButRequiredWaiverQuestions = (
    questions: Array<SurveyQuestion>,
) =>
    questions.filter(
        (question) =>
            !(
                isWaiverQuestion(question.type) &&
                isRequiredQuestion(question, questions)
            ),
    );

export const getRequiredQuestions = (questions: Array<SurveyQuestion>) =>
    questions.filter((question) => isRequiredQuestion(question, questions));

export const getRequiredAttendeesQuestions = (
    questionsForAttendees: Array<AttendeeSurvey>,
) =>
    questionsForAttendees.map((attendee) => ({
        ...attendee,
        survey: getRequiredQuestions(attendee.survey),
    }));

export const getOptionalAttendeesQuestions = <T extends AttendeeSurvey>(
    questionsForAttendees: Array<T>,
) =>
    questionsForAttendees.map((attendee) => ({
        ...attendee,
        survey: getOptionalQuestions(attendee.survey),
    }));

export const getOptionalAndCannedAttendeesQuestions = (
    questionsForAttendees: Array<AttendeeSurvey>,
) =>
    questionsForAttendees.map((attendee) => ({
        ...attendee,
        survey: getOptionalAndCannedQuestions(attendee.survey),
    }));

export const getAllButRequiredWaiverAttendeesQuestions = (
    questionsForAttendees: Array<AttendeeSurvey>,
) =>
    questionsForAttendees.map((attendee) => ({
        ...attendee,
        survey: getAllButRequiredWaiverQuestions(attendee.survey),
    }));

export const getQuestionsByCustomisation = ({
    isRequired,
    questionCustomizationFilter,
    questions,
}: {
    isRequired: boolean;
    questionCustomizationFilter: QuestionCustomisationFilterType;
    questions: Array<SurveyQuestion>;
}) =>
    questions.filter((singleQuestion) => {
        const isMatchingCustomizationFilter = singleQuestion.name.startsWith(
            questionCustomisationFilterPrefix[questionCustomizationFilter],
        );

        if (!isMatchingCustomizationFilter) {
            return false;
        }

        const singleQuestionIsRequired = isRequiredQuestion(
            singleQuestion,
            questions,
        );

        return isRequired === singleQuestionIsRequired;
    });
